"use client";
import useTranslate, { useTranslationMap } from "@app/_hooks/useTranslate";
import { CheckIcon } from "lucide-react";

import getStockStatusText from "@/utils/product/getStockStatusText";
import Link from "@components/Link";
import { useMarket } from "@context/marketContext";
import useProductQueries from "@server/hooks/useProductQueries";
import Image from "next/image";
import { type ReactNode, useEffect } from "react";
import type { Variant } from "types/product";

import { cn } from "@/lib/utils";
import { formatPrice } from "@/utils/priceUtils";

const Lightsource = ({
  id,
  renderImage,
  renderContent,
  checked,
  onChange,
}: {
  id: string;
  renderImage: () => ReactNode | null;
  renderContent: () => ReactNode | null;
  checked: boolean;
  onChange: () => void;
}) => (
  <div className="border-gray-150 relative border-x border-t last:border-b">
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="peer absolute top-0 left-0 h-0 w-0"
      value={id}
      id={id}
    />
    <label
      htmlFor={id}
      className="group flex cursor-pointer items-center transition peer-checked:bg-gray-100 hover:bg-gray-100/50"
      aria-selected={checked}
    >
      <div className="flex aspect-square w-[100px] shrink-0 items-center justify-center self-stretch bg-gray-100">
        {renderImage()}
      </div>

      <div className="relative ml-3 h-4 w-4 shrink-0 rounded-full border border-black group-aria-selected:bg-black">
        <CheckIcon
          strokeWidth={1}
          className="absolute top-0 -left-px size-4 text-white opacity-0 transition group-aria-selected:opacity-100"
        />
      </div>

      {renderContent()}
    </label>
  </div>
);

/**
 * @todo Refactor this component with better separation of concerns
 * We also seem to have problems with loops and re-renders :')
 */
export default function Lightsources({
  variant,
  lightsources,
  lightsource = lightsources.at(0),
  className,
}: {
  variant: Variant;
  lightsource?: Variant | null;
  lightsources: (Variant & { isRecommended: boolean })[];
  className?: string;
}) {
  const t = useTranslate();
  const dictionary = useTranslationMap();
  const { locale, contentfulLocale: contentfulLanguage } =
    useMarket().state.market;
  const setQueries = useProductQueries();

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);

    if (!queryParams.has("lightsource")) {
      setQueries({
        type: "lightsource",
        lightsourceId: lightsources[0].id,
      });
    }
  }, [lightsources, setQueries]);

  return (
    <div className={cn("", className)}>
      <p className="my-3 block text-base">
        {t("productComesWithoutLightSourceAdd")}
      </p>
      {lightsources?.map((item) => {
        const stockStatusText = getStockStatusText({
          isShortened: true,
          locale: contentfulLanguage,
          dictionary,
          stockStatus: item.stockStatus ?? variant.stockStatus,
          nextDeliveryDate: item.nextDeliveryDate ?? variant.nextDeliveryDate,
        });

        return (
          <Lightsource
            key={item.id}
            checked={lightsource?.id === item.id}
            onChange={() => {
              setQueries({
                type: "lightsource",
                lightsourceId: item.id,
              });
            }}
            id={item.id}
            renderImage={() =>
              item?.images?.[0].src ? (
                <Image
                  className="object-contain mix-blend-multiply"
                  src={item.images[0].src}
                  alt={item.images[0].alt || ""}
                  width={100}
                  height={100}
                />
              ) : null
            }
            renderContent={() => (
              <>
                <div className="grow space-y-1 p-3 text-sm md:space-y-1.5">
                  <p className="uppercase">{item.norce_brand}</p>
                  <p className="text-sm">{item.title}</p>
                  <div className="flex text-sm font-normal md:hidden">
                    <span>
                      {variant?.lightsourceQuantity || 1}
                      &nbsp;x&nbsp;
                    </span>
                    <span>{formatPrice(item.price, locale)}</span>
                    {item.campaignPrice && item.originalPrice && (
                      <span className="ml-1 font-normal text-gray-300 line-through">
                        &nbsp;
                        {formatPrice(item.originalPrice, locale)}
                      </span>
                    )}
                  </div>
                  <p className="uppercase">{stockStatusText}</p>
                </div>
                <div className="shrink-0 space-y-1 self-start p-3 text-right text-sm md:self-auto">
                  {item.link && (
                    <Link
                      locale={locale}
                      className="uppercase underline"
                      href={item.link}
                    >
                      {t("details")}
                    </Link>
                  )}
                  <div className="hidden justify-end font-normal md:flex">
                    <span>
                      {variant?.lightsourceQuantity || 1}
                      &nbsp;x&nbsp;
                    </span>
                    <span>{formatPrice(item.price, locale)}</span>
                    {item.campaignPrice && item.originalPrice && (
                      <span className="ml-1 font-normal text-gray-300 line-through">
                        &nbsp;
                        {formatPrice(item.originalPrice, locale)}
                      </span>
                    )}
                  </div>
                  {item.isRecommended && (
                    <div className="absolute right-3 bottom-3 flex items-center border border-black p-1 text-sm md:static">
                      <CheckIcon strokeWidth={1} className="mr-1 size-4.5" />
                      {t("recommended")}
                    </div>
                  )}
                </div>
              </>
            )}
          />
        );
      })}
      <Lightsource
        id="no-lightsource"
        checked={!lightsource}
        onChange={() => {
          setQueries({
            type: "lightsource",
            lightsourceId: "no-lightsource",
          });
        }}
        renderImage={() => (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="64"
            height="64"
            viewBox="0 0 64 64"
            fill="none"
          >
            <path
              d="M31.9886 14.8691C37.6627 14.8691 42.2665 19.5412 42.2665 25.2729C42.2665 28.1489 41.5677 29.8091 40.7929 31.1673C40.0179 32.5254 39.2073 33.5523 38.8281 35.3744C38.7829 35.5935 38.5685 35.7342 38.3494 35.6889C38.1302 35.6437 37.9894 35.4296 38.0346 35.2104C38.4483 33.2225 39.3598 32.0619 40.1002 30.7644C40.8406 29.4669 41.4604 28.0232 41.4604 25.2732C41.4604 19.972 37.2218 15.6758 31.9889 15.6758C28.4768 15.6758 25.4129 17.6146 23.7766 20.4875C23.7302 20.5921 23.6412 20.6717 23.5325 20.7069C23.4235 20.7421 23.3048 20.7294 23.2058 20.6717C23.1069 20.6142 23.0371 20.5173 23.0137 20.4052C22.9906 20.2931 23.016 20.1765 23.0839 20.0842C24.8573 16.9706 28.1804 14.8691 31.9886 14.8691ZM18.4617 17.6904C18.5535 17.7035 18.6381 17.7481 18.701 17.8164L31.2712 30.3737L45.8434 44.946C45.9324 45.0179 45.9872 45.1241 45.9932 45.2383C45.9995 45.3527 45.9568 45.4641 45.8757 45.545C45.7949 45.626 45.6834 45.6687 45.5691 45.6625C45.4549 45.6564 45.3486 45.6021 45.2768 45.5129L38.4373 38.6734L37.0015 46.1172C36.8662 46.8188 36.1715 47.2507 35.4396 47.2507H35.2758V47.5026C35.2758 48.3617 34.6867 49.1274 33.8652 49.1274H30.1119C29.2905 49.1274 28.7013 48.3618 28.7013 47.5026V47.2507H28.5375C27.8056 47.2507 27.1233 46.819 26.9884 46.1172L25.4013 37.8548C25.3969 37.8466 25.3927 37.8383 25.3888 37.8298V37.8173C25.3708 37.7689 25.3623 37.7177 25.3636 37.666C25.3636 34.6806 24.5152 33.3704 23.6003 31.8596C22.6851 30.349 21.7109 28.6577 21.7109 25.2719C21.7109 24.2664 21.8625 23.2958 22.1265 22.375L18.1338 18.3823C18.0071 18.2625 17.9719 18.075 18.0465 17.9173C18.1211 17.7596 18.2887 17.6687 18.4617 17.6904ZM22.7819 23.0306C22.6106 23.7517 22.5173 24.4971 22.5173 25.2725C22.5173 28.5098 23.3831 29.9419 24.2931 31.4442C25.1589 32.8736 26.0444 34.412 26.1321 37.2634H37.0142L22.7819 23.0306ZM37.7451 38.0696H26.2459L26.6742 40.2485C26.6786 40.2483 26.6827 40.2483 26.6869 40.2485H34.0426C34.0551 40.2479 34.0679 40.2479 34.0804 40.2485C34.3024 40.2593 34.4739 40.4481 34.4635 40.6704C34.4531 40.8925 34.2647 41.0643 34.0426 41.0545H26.8256L27.2539 43.2587H31.675C31.6916 43.2577 31.7083 43.2577 31.7252 43.2587C31.946 43.2747 32.1129 43.4656 32.0989 43.6866C32.0852 43.9077 31.8958 44.0764 31.6748 44.0647H27.4177L27.7829 45.9666C27.819 46.1539 28.1552 46.4452 28.5385 46.4452H35.4409C35.8243 46.4452 36.173 46.1539 36.2093 45.9666L37.708 38.1703C37.7162 38.1351 37.7291 38.1012 37.7462 38.0695L37.7451 38.0696ZM34.4703 47.2514H29.5076V47.5033C29.5076 48.0098 29.8184 48.3221 30.1122 48.3221H33.8655C34.1592 48.3221 34.4701 48.01 34.4701 47.5033L34.4703 47.2514Z"
              fill="#757575"
            />
          </svg>
        )}
        renderContent={() => (
          <p className="p-3 text-sm">{t("noLightSourcePlease")}</p>
        )}
      />
    </div>
  );
}
